/* Detail — the one place an individual saved item is consumed (§9).
 *
 * Not a workspace: there is no rail entry and it is absent from WORKSPACES. It
 * is an overlay the shell mounts above whichever workspace opened it, which is
 * what makes §10 structural — the workspace underneath is never torn down, so
 * "Back" restores its scroll, filters and map viewport without anything having
 * been saved or replayed.
 *
 * Three call sites open it and each knows a different amount about what it is
 * handing over: Library has full items, Map has a venue's items, Index has bare
 * ids. So the contract is the intersection — **a list of ids and a position in
 * it** — and Detail fetches the rest. That is not a compromise: /api/reel/{id}
 * is the only source of `shortcode`, which the player needs, so the fetch would
 * happen regardless of how much the caller passed along.
 *
 * The playlist itself lives in the shell, not here. Detail asks to move and the
 * shell moves it, so the URL and the position can never disagree.
 *
 * Two arrangements (ADR 0006 for the slab, ADR 0013 for the phone):
 *
 * - **Desktop** is one centred slab, video beside knowledge, controls belonging
 *   to the frame rather than to a pane.
 * - **Phone** is a full-bleed player with the knowledge on a sheet over it. The
 *   axes are split so two gestures can coexist: the frame owns vertical (that
 *   is the playlist) and the sheet owns its own band. Nothing else on this
 *   screen may claim a vertical drag.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query as domQuery } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { BP_DESKTOP } from '../../design/tokens.js';
import type { ItemDetail } from '../../core/types.js';
import type { GDialog } from '../../component/g-dialog/g-dialog.js';
import { deleteItem, getItemDetail, isSignedIn, warmVideo } from '../../core/api.js';
import { clean, isStillsItem } from '../../../../packages/core/index.js';
import type { PlayerSurface } from './player-surface.js';
import './player-surface.js';
import './knowledge-panel.js';
import '../../component/g-button/g-button.js';
import '../../component/g-dialog/g-dialog.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';
import '../../component/g-sheet/g-sheet.js';

/** How many items ahead to pre-resolve. Two, as in v1's `warmNeighbours`. */
const WARM_AHEAD = 2;

/** Closed, half over the player, or nearly all of it. */
type SheetState = 'closed' | 'half' | 'full';

/**
 * The sheet's detents, and the ladder a drag walks.
 *
 * All three are rungs — unlike Map, where the venue view is modal — because
 * this sheet has exactly one subject and every stop shows more of it. Full
 * stops at 0.93 so a strip of player is always left to tap on, which is the
 * dismiss affordance that replaces a scrim.
 */
const DETENTS: Record<SheetState, number> = { closed: 0, half: 0.5, full: 0.93 };
const LADDER: SheetState[] = ['closed', 'half', 'full'];

/**
 * Height of the title + Detail band, which the player's controls sit above.
 *
 * Two stacked lines: the title, then the chevron pill under it. The whole band
 * is one button — the title is not decoration you have to aim past to reach a
 * small control, it is part of the same target.
 */
const STRIP_H = 62;

/** A vertical drag past this, quicker than FLICK_MS, steps the playlist. */
const FLICK_PX = 60;
const FLICK_MS = 600;

@customElement('g-detail')
export class GDetail extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: flex;
        position: fixed;
        inset: 0;
        z-index: 40;
        flex-direction: column;
        background: var(--bg);
      }
      .top {
        display: flex;
        flex: none;
        align-items: center;
        gap: var(--space-2);
        height: var(--header-h);
        padding: 0 var(--space-3);
        border-bottom: 1px solid var(--border);
      }
      /* The origin line answers "what am I paging through" — a venue, an Index
         entry, a category, a query. The icon says which workspace it came from,
         so the label itself can be just the thread and stay quiet (§31). */
      .origin {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        min-width: 0;
        color: var(--text-dim);
        font-family: var(--font-ui);
        font-size: 0.85rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        --icon-size: 14px;
      }
      .spacer {
        flex: 1;
      }
      /* The video and the knowledge are the two halves of one item, so they are
         one object: a single bordered slab, centred, with the leftover width as
         page margin rather than as a black field the video floats in. */
      .panes {
        display: flex;
        flex: 1;
        justify-content: center;
        min-height: 0;
        padding: var(--space-5);
        overflow: hidden;
      }
      .stack {
        position: relative;
        display: flex;
        max-width: 100%;
      }
      .slab {
        display: flex;
        max-width: 100%;
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
      /* Height-driven, so the frame is exactly 9:16 and the slab's width — and
         therefore the panel's position — never changes while paging. A
         landscape video letterboxes top and bottom inside it, which is the
         deliberate trade: a stable reading column beats a snug fit. */
      .player-wrap {
        position: relative;
        flex: none;
        aspect-ratio: 9 / 16;
        min-width: 0;
      }
      player-surface {
        width: 100%;
        height: 100%;
      }
      /* Straddles the slab's outer edge. Vertical because the playlist is a
         vertical feed and up/down are the primary keys — and one object, so the
         eye finds it once, rather than a pair flanking the slab (which would
         read as a carousel swapping peers, not a cursor into a list). */
      .nav {
        position: absolute;
        top: 50%;
        left: 0;
        z-index: 2;
        display: flex;
        flex-direction: column;
        transform: translate(-50%, -50%);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        overflow: hidden;
      }
      .nav button {
        display: grid;
        place-items: center;
        width: 34px;
        height: 34px;
        padding: 0;
        border: 0;
        background: none;
        color: var(--text);
        cursor: pointer;
        --icon-size: 16px;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
      @media (hover: hover) {
        .nav button:hover {
          background: var(--surface-2);
        }
      }
      .nav button:active {
        background: var(--press);
      }
      .nav button + button {
        border-top: 1px solid var(--border);
      }
      knowledge-panel {
        width: 420px;
        flex: none;
        overflow-y: auto;
        overscroll-behavior: contain;
        border-left: 1px solid var(--border);
        scrollbar-width: thin;
        scrollbar-color: var(--border) transparent;
      }
      knowledge-panel::-webkit-scrollbar {
        width: 10px;
      }
      knowledge-panel::-webkit-scrollbar-track {
        background: transparent;
      }
      knowledge-panel::-webkit-scrollbar-thumb {
        border: 3px solid transparent;
        border-radius: var(--radius-full);
        background: var(--border);
        background-clip: content-box;
      }
      .failed {
        display: grid;
        place-items: center;
        flex: 1;
        padding: var(--space-6);
      }
      .warn {
        margin: 0 0 var(--space-2);
        font-size: 0.9rem;
        line-height: 1.45;
      }
      /* The title and the way into the knowledge, over the frame's bottom edge.
         Desktop has the whole knowledge column in the open and needs neither. */
      .strip {
        display: none;
      }

      /* ── Phone (ADR 0013) ────────────────────────────────────────────
         Full-bleed player, in-air controls, knowledge on a sheet.

         No bars: the player's own scrim already keeps controls legible over an
         arbitrary frame, so everything floats on it. A band across the top and
         the bottom would draw two horizontals through the reel and make it feel
         smaller than the screen it actually has. The rule that keeps this
         coherent is "capsule means tappable" — controls and readouts get the
         dark pill, labels ride the scrim bare. */
      @media (max-width: 959px) {
        .panes {
          position: relative;
          padding: 0;
          overflow: hidden;
        }
        /* The wrappers survive the breakpoint so player-surface keeps its
           position in the template — a fresh element here would tear down the
           <video>, drop the resolved CDN URL and re-fetch it on every resize
           past 960px. They just stop shaping anything. */
        .stack,
        .slab {
          display: block;
          flex: 1;
          min-width: 0;
          max-width: none;
          border: 0;
          border-radius: 0;
          overflow: visible;
        }
        .stack {
          position: static;
        }
        /* Sized to the band the sheet leaves, and animated ONLY on a committed
           detent change — never while a finger is dragging. The sheet moves on
           a compositor transform; matching it with per-frame height writes here
           would be layout on every frame. The gap that opens mid-drag is
           invisible because what shows through it is the player's own black. */
        .player-wrap {
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          aspect-ratio: auto;
          height: var(--player-h, 100%);
          transition: height 300ms cubic-bezier(0.32, 0.72, 0, 1);
          --ctrl-lift: ${STRIP_H}px;
          /* The frame owns vertical, so the browser must be told not to want
             it. Without this Chrome claims the touch and fires pointercancel
             a few pixels into every swipe — measured on the device:
             pointerdown, touchmove, pointercancel, and pointerup never
             arrives, so the flick can never be recognised. There is nothing to
             scroll here; Chrome takes it anyway. Same shape as ADR 0012's
             non-passive preventDefault, solved declaratively because this
             element has no scroller inside it to arbitrate with.

             pan-x AND pinch-zoom, not none: pinch-zoom is the accessibility
             escape hatch ADR 0010 §7 refused to spend, and naming it
             explicitly keeps it while still releasing the vertical axis. */
          touch-action: pan-x pinch-zoom;
        }
        @media (prefers-reduced-motion: reduce) {
          .player-wrap {
            transition: none;
          }
        }
        /* In-air: no border, no surface, and clear of the status bar the video
           itself bleeds under.

           The band starts at the TRUE top and absorbs the inset as padding
           rather than being offset by it. Offsetting leaves the status-bar strip
           belonging to the frame underneath, so a tap aimed a few pixels high of
           Back misses this band entirely and toggles play/pause instead — which
           reads as the back button doing something random. The band has to
           cover every pixel above its own controls. */
        .top {
          position: absolute;
          z-index: 2;
          top: 0;
          right: 0;
          left: 0;
          height: calc(48px + var(--safe-t, 0px));
          padding-top: var(--safe-t, 0px);
          border-bottom: 0;
          /* The remaining-time pill lives in player-surface and holds the right
             end of this same band across a shadow boundary, so the thread stops
             short of it rather than running underneath. */
          padding-right: 72px;
          color: #fff;
        }
        .top .origin {
          color: rgb(255 255 255 / 0.85);
          text-shadow: 0 1px 3px rgb(0 0 0 / 0.6);
        }
        /* 44px, up from the 38 the sweep measured. The band absorbs a miss, but
           a bigger target is what stops there being one. */
        .top g-button {
          --g-button-pad: 13px;
          --icon-size: 18px;
        }
        /* Gone whenever a sheet is up — the sheet carries its own close, which
           is what makes hiding the only other way out safe. */
        :host([sheeted]) .top {
          display: none;
        }
        /* One button, not a label beside a control: the title says what you are
           about to open, so making it part of the target costs nothing and
           turns a 36px pill into a full-width band. */
        .strip {
          display: flex;
          position: absolute;
          right: 0;
          bottom: calc(var(--overhang, 0px) + 24px);
          left: 0;
          z-index: 1;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 6px;
          min-height: ${STRIP_H}px;
          padding: 0 var(--content-r) 0 var(--content-l);
          border: 0;
          background: none;
          text-align: left;
          cursor: pointer;
          transition: background-color 120ms ease;
          /* touch-action intersects down the hit-test chain, so this button
             inherits .player-wrap's pan-x pinch-zoom (kept there on purpose,
             ADR 0013 §3) — and keeping pinch-zoom legal keeps the browser's
             double-tap-to-zoom disambiguation alive too. Right after the swipe
             that switches reels, a fast follow-up tap here landed inside that
             disambiguation window and got held back rather than dispatched.
             manipulation can only narrow what the parent already allows, so
             it doesn't hand this button any pan/zoom the frame refused it —
             it only opts this specific control out of the double-tap wait. */
          touch-action: manipulation;
        }
        /* Fixed white, not --press: the strip sits ON the video inside the
           scrim, so themed ink would be near-white on a dark frame in dark mode
           and invisible. Weak on purpose — it spans the full width, and at the
           wash the rows use it would read as the sheet already opening. */
        .strip:active {
          background: rgb(255 255 255 / 0.09);
        }
        .ttl {
          max-width: 100%;
          color: #fff;
          font-family: var(--font-ui);
          font-size: 0.9rem;
          font-weight: 600;
          text-shadow: 0 1px 3px rgb(0 0 0 / 0.7);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* The chevron is doing the work the removed peek band used to do: it is
           the only thing on screen saying there is something below. A span, not
           a button — .strip is the button now, so this needs no target of its
           own and gets none. */
        .open-detail {
          display: inline-flex;
          flex: none;
          align-items: center;
          gap: 6px;
          min-height: 30px;
          padding: 0 var(--space-3);
          border-radius: var(--radius-full);
          background: rgb(0 0 0 / 0.55);
          color: #fff;
          font-family: var(--font-ui);
          font-size: 0.82rem;
          font-weight: 600;
          --icon-size: 16px;
        }
        /* Kept in the DOM and taken off the screen, not removed. The playlist is
           a swipe here, and ADR 0010 §2 settled that a gesture-only affordance
           is no affordance at all for anyone using assistive tech — these are
           what a screen reader and the arrow keys still reach. */
        .nav {
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          border: 0;
          overflow: hidden;
          clip-path: inset(50%);
          white-space: nowrap;
        }
        /* flex has to be restated here. g-sheet's own ::slotted(*) flex:1 rule
           loses to the desktop knowledge-panel flex:none rule above, because
           an outer author rule beats a shadow rule for a slotted element — so
           without this the panel sizes to its content and runs straight out of
           the pane the sheet just clamped. Measured at the half detent: a 382px
           pane holding a 532px panel. */
        g-sheet knowledge-panel {
          flex: 1;
          min-height: 0;
          width: 100%;
          border-left: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
        }
      }
    `,
  ];

  /** The playlist, as item ids. Owned by the shell. */
  @property({ attribute: false }) ids: number[] = [];
  /** Position in `ids`. */
  @property({ type: Number }) at = 0;
  /** The workspace this was opened from. Shown when there is no thread. */
  @property() origin = '';
  /**
   * What this playlist has in common — a venue, an Index entry, a category, a
   * query. Empty when the list has no thread (unfiltered Library, deep link),
   * in which case the workspace name stands in.
   */
  @property() thread = '';
  /** Icon for the origin workspace. Resolved by the shell — see ADR 0002. */
  @property() icon = '';
  /** Reflected so the in-air strips can hide themselves in CSS. */
  @property({ type: Boolean, reflect: true }) sheeted = false;

  @state() private detail: ItemDetail | null = null;
  @state() private loading = true;
  @state() private error = '';
  @state() private confirming = false;
  /**
   * Below the breakpoint, where the knowledge is a sheet.
   *
   * Watched at runtime rather than left to CSS: whether a tap on the frame
   * plays the video or dismisses a sheet is behaviour, not arrangement, and no
   * media query can say it. Same call `g-map` made.
   */
  @state() private narrow = false;
  @state() private sheet: SheetState = 'closed';

  @domQuery('g-dialog') private dialog?: GDialog;
  @domQuery('player-surface') private player?: PlayerSurface;

  private mq = window.matchMedia(`(max-width: ${BP_DESKTOP - 1}px)`);

  /** Fetched details, keyed by id. Neighbours land here before they're needed. */
  private cache = new Map<number, ItemDetail>();
  private token = 0;

  /**
   * Whether an open sheet has a history entry standing behind it.
   *
   * The system back button has to close the sheet before it leaves the item —
   * on Android it is the primary dismiss gesture, and a sheet that ignores it
   * reads as a trap. Same mechanism as Map's (g-map.ts): push a same-URL entry
   * on open, pop it when the sheet closes by any other means, so the stack
   * balances either way. One entry covers both open detents, so Back from full
   * leaves the sheet entirely rather than stepping down to half.
   */
  private sheetEntry = false;

  // Flick bookkeeping for the playlist gesture.
  private flickY = 0;
  private flickX = 0;
  private flickAt = 0;
  private flickId: number | null = null;
  /** A gesture already acted; swallow the click it is about to produce. */
  private swallowClick = false;

  private get currentId(): number | undefined {
    return this.ids[this.at];
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.narrow = this.mq.matches;
    this.mq.addEventListener('change', this.onBreakpoint);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('resize', this.measureOverhang);
    this.measureOverhang();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mq.removeEventListener('change', this.onBreakpoint);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('resize', this.measureOverhang);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('sheet') || changed.has('narrow')) {
      this.sheeted = this.narrow && this.sheet !== 'closed';
      // The player's height is the complement of the sheet's cover. Derived
      // from the same table the sheet reads so the two cannot drift; written
      // here, once per committed state, never during a drag.
      const cover = this.narrow ? DETENTS[this.sheet] : 0;
      this.style.setProperty('--player-h', `${Math.round((1 - cover) * 100)}%`);
    }
    if (!changed.has('ids') && !changed.has('at')) return;
    void this.load();
    // Stacked layout only: below the breakpoint `.panes` is the scroll port
    // rather than the panel, so the panel resetting its own offset isn't
    // enough. Harmless on desktop, where this never scrolls.
    const panes = this.renderRoot.querySelector('.panes');
    if (panes) panes.scrollTop = 0;
  }

  /**
   * How much of a fixed overlay falls past the visible viewport.
   *
   * In the installed PWA the layout viewport is taller than what you can see —
   * measured at 775 against 719 (ADR 0010 §5) — so `inset: 0` overhangs by the
   * system navigation bar's height. The video is welcome to bleed into that;
   * the controls are not, or the progress bar ends up unhittable behind the
   * gesture pill. `env(safe-area-inset-bottom)` cannot stand in: it reports 0
   * there, because a nav bar is not a display cutout.
   */
  private measureOverhang = (): void => {
    const over = Math.max(0, window.innerHeight - document.body.clientHeight);
    this.style.setProperty('--overhang', `${over}px`);
  };

  private onBreakpoint = (e: MediaQueryListEvent): void => {
    this.narrow = e.matches;
    // Landing on desktop with a sheet open would leave the knowledge column
    // rendered twice. The pop still runs, so the stack balances.
    if (!e.matches) this.setSheet('closed');
  };

  /**
   * Back closed the sheet. Clear the marker FIRST, so the close path below sees
   * no entry to pop and does not send the history stack backwards twice.
   */
  private onPopState = (): void => {
    if (!this.sheetEntry) return;
    this.sheetEntry = false;
    if (this.sheet !== 'closed') this.sheet = 'closed';
  };

  /**
   * Move the sheet, keeping the back stack in step.
   *
   * The `history.state` check on the way out matters: by then the user may have
   * pushed something else on top, and an unguarded `back()` would undo that
   * instead of our own entry.
   */
  private setSheet(next: SheetState): void {
    const wasOpen = this.sheet !== 'closed';
    const isOpen = next !== 'closed';
    this.sheet = next;
    // Full covers the frame, so the video is decoding behind an opaque surface
    // for no one. Closing does NOT resume — see PlayerSurface.pause().
    if (next === 'full') this.player?.pause();
    if (isOpen === wasOpen) return;
    if (isOpen) {
      // Only the push is gated on the breakpoint; the pop below always runs, so
      // crossing to desktop with a sheet open still balances the stack.
      if (!this.narrow) return;
      // `overlay` is restated rather than spread: the sheet genuinely IS inside
      // Detail, so `Router.onItem()` must still be true here — but nothing else
      // from the entry below should ride along (ADR 0021 decision 2).
      history.pushState({ overlay: true, detailSheet: true }, '');
      this.sheetEntry = true;
    } else if (this.sheetEntry) {
      this.sheetEntry = false;
      if ((history.state as { detailSheet?: boolean } | null)?.detailSheet) history.back();
    }
  }

  /** Collapse the sheet and wait for the history pop to land, if there is one. */
  private async collapseSheet(): Promise<void> {
    if (!this.sheetEntry) {
      this.sheet = 'closed';
      return;
    }
    const landed = new Promise<void>((resolve) => {
      const done = (): void => {
        window.removeEventListener('popstate', done);
        resolve();
      };
      window.addEventListener('popstate', done);
      // A traversal that never arrives must not wedge a delete.
      setTimeout(done, 300);
    });
    this.setSheet('closed');
    await landed;
  }

  /**
   * Keyboard, on window rather than on the host: this is a full-screen overlay
   * and the keys have to work wherever focus happens to be, including on the
   * player's own buttons.
   *
   * Both axes move the playlist. Up/down is v1's (and matches a vertical feed),
   * left/right is what a desktop user reaches for first, and neither has another
   * job here — the video seeks by pointer only.
   */
  private onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    // Never swallow keys aimed at a control that wants them.
    const el = e.composedPath()[0] as HTMLElement | undefined;
    if (el?.matches?.('input, textarea, select, [contenteditable]')) return;

    if (e.key === 'Escape') {
      // A dialog is a layer of its own; Escape belongs to it first.
      if (this.confirming) return;
      e.preventDefault();
      this.close();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      this.step(1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      this.step(-1);
    }
  };

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  /**
   * One step out, not all the way.
   *
   * With a sheet open this closes the sheet and nothing else, which is what
   * makes the system back button behave the way Android users expect — and it
   * also guarantees the overlay can only ever be dismissed with the history
   * stack already balanced.
   */
  private close(): void {
    if (this.narrow && this.sheet !== 'closed') {
      this.setSheet('closed');
      return;
    }
    this.emit('close');
  }

  /**
   * Move by `delta`, wrapping at both ends.
   *
   * A playlist is a ring, so there is no disabled state and no last item — past
   * the end is the beginning. That also means position is no longer legible
   * from the controls, which is accepted: the counter was removed deliberately,
   * and "where am I in this list" is not a question this screen exists to
   * answer (§23 — surface only what's actionable).
   */
  private step(delta: number): void {
    const n = this.ids.length;
    if (n < 2) return;
    this.emit('navigate', (((this.at + delta) % n) + n) % n);
  }

  // ── the frame's gestures ───────────────────────────────────
  //
  // The frame owns the vertical axis and the sheet owns its own band; that
  // split is what lets a reel-style swipe and a drag-up sheet coexist without
  // either having to guess which one the finger meant.

  private onFrameDown = (e: PointerEvent): void => {
    if (!this.narrow || !e.isPrimary) return;
    // Disarm here, not on a timer. A touch swipe fires no click at all, so a
    // flag set by the last gesture would still be armed when the user next
    // taps something — measured on the device: swipe to the next item, then
    // tap the title, and the sheet refuses to open because the tap was eaten
    // by a swipe that ended seconds ago.
    this.swallowClick = false;
    this.flickId = e.pointerId;
    this.flickY = e.clientY;
    this.flickX = e.clientX;
    this.flickAt = performance.now();
  };

  /**
   * A flick, recognised on release — no live tracking and no per-frame
   * transform writes.
   *
   * ADR 0010 reverted the shell's swipe because tracking a drag frame by frame
   * measured as laggy on the reference device, and named this as the lighter
   * alternative. A playlist step has nothing to animate on the way, so there is
   * genuinely nothing to track: read the delta once, at the end.
   */
  private onFrameUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.flickId) return;
    this.flickId = null;
    if (this.sheet !== 'closed') return;
    const dy = e.clientY - this.flickY;
    const dx = e.clientX - this.flickX;
    if (Math.abs(dy) < FLICK_PX || Math.abs(dy) <= Math.abs(dx)) return;
    if (performance.now() - this.flickAt > FLICK_MS) return;
    // Up for the next item, as every vertical feed does it.
    this.swallowClick = true;
    this.step(dy < 0 ? 1 : -1);
  };

  private onFrameCancel = (e: PointerEvent): void => {
    if (e.pointerId === this.flickId) this.flickId = null;
  };

  /**
   * Every click on the frame, seen before player-surface's own handler.
   *
   * Bound in the template with `capture: true` rather than imperatively, so it
   * is re-attached with the element on every render — the wrapper disappears
   * whenever the detail fetch fails, and a listener attached once in
   * `firstUpdated` would not come back with it.
   *
   * Two jobs, both of which have to happen above the shadow boundary: a
   * completed swipe still fires a click, which would toggle play/pause as a
   * parting gift; and with a sheet up, the exposed strip of player is the
   * dismiss target rather than the play button.
   */
  private onFrameClick = (e: MouseEvent): void => {
    if (!this.narrow) return;
    if (this.swallowClick) {
      this.swallowClick = false;
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (this.sheet === 'closed') return;
    e.stopPropagation();
    e.preventDefault();
    this.setSheet(this.sheet === 'full' ? 'half' : 'closed');
  };

  private async load(): Promise<void> {
    const id = this.currentId;
    if (id === undefined) return;
    const mine = ++this.token;

    const cached = this.cache.get(id);
    if (cached) {
      this.detail = cached;
      this.loading = false;
      this.error = '';
    } else {
      // Blanking first is deliberate: showing the previous item's transcript
      // under the next item's video, even for 200ms, is worse than showing
      // nothing. The player has its own loading state and is unaffected.
      this.detail = null;
      this.loading = true;
      this.error = '';
      try {
        const d = await getItemDetail(id);
        if (mine !== this.token) return;
        if (isSignedIn(d)) {
          this.cache.set(id, d);
          this.detail = d;
        } else {
          this.error = 'Your session expired. Reload to sign in again.';
        }
      } catch (e) {
        if (mine !== this.token) return;
        this.error = e instanceof Error ? e.message : String(e);
      }
      this.loading = false;
    }
    this.warmNeighbours();
  }

  /**
   * Pre-fetch what comes next, both halves of it: the detail from our own API,
   * and — via the server — the CDN URL, which is the slow one because it costs a
   * yt-dlp resolve. Failures are ignored by construction; this is entirely an
   * optimisation and must never surface.
   */
  private warmNeighbours(): void {
    for (let i = 1; i <= WARM_AHEAD; i += 1) {
      const id = this.ids[this.at + i];
      if (id === undefined || this.cache.has(id)) continue;
      void getItemDetail(id)
        .then((d) => {
          if (isSignedIn(d)) {
            this.cache.set(id, d);
            // The detail half only, for an image post: it has no video, and
            // warming one is a request the server can only refuse (ADR 0049).
            if (!isStillsItem(d)) warmVideo(d.shortcode);
          }
        })
        .catch(() => {});
    }
  }

  private askDelete(): void {
    this.confirming = true;
    void this.updateComplete.then(() => this.dialog?.show());
  }

  private async confirmDelete(): Promise<void> {
    const d = this.detail;
    this.dialog?.close();
    this.confirming = false;
    if (!d) return;
    try {
      await deleteItem(d.id);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
      return;
    }
    // Remove lives at the foot of the sheet, so this is the one path that can
    // close the overlay with a sheet still open. Collapse first and let the pop
    // land, or the sheet's history entry outlives the thing it belonged to.
    await this.collapseSheet();
    // Tell everyone else first — the workspaces underneath are holding this item
    // in their own lists and the shell drops it from the playlist, which may
    // close this overlay outright if it was the only thing in it.
    this.emit('item-deleted', d.id);
  }

  private renderKnowledge(d: ItemDetail | null) {
    return html`<knowledge-panel
      .detail=${d}
      .loading=${this.loading}
      @show-place=${(e: CustomEvent<number>) => {
        this.emit('show-place', e.detail);
      }}
      @remove=${this.askDelete}
    ></knowledge-panel>`;
  }

  override render() {
    const n = this.ids.length;
    const d = this.detail;
    const title = clean(d?.title ?? '') || 'Untitled';
    const open = this.narrow && this.sheet !== 'closed';
    return html`
      <div class="top">
        <g-button icon-only label="Back" @click=${this.close}>
          <g-icon name="back"></g-icon>
        </g-button>
        ${this.thread || this.origin
          ? html`<span class="origin" title=${this.thread ? this.origin : ''}>
              ${this.icon ? html`<g-icon name=${this.icon}></g-icon>` : nothing}
              ${this.thread || this.origin}
            </span>`
          : nothing}
        <span class="spacer"></span>
      </div>

      ${this.error && !d
        ? html`<div class="failed"><g-notice tone="danger">${this.error}</g-notice></div>`
        : html`
            <div class="panes">
              <div class="stack">
                <div class="slab">
                  <div
                    class="player-wrap"
                  @pointerdown=${this.onFrameDown}
                  @pointerup=${this.onFrameUp}
                  @pointercancel=${this.onFrameCancel}
                    @click=${{ handleEvent: this.onFrameClick, capture: true }}
                  >
                    <player-surface
                      .shortcode=${d?.shortcode ?? ''}
                      .url=${d?.url ?? ''}
                      .poster=${d?.thumb ?? ''}
                      .mediaType=${d?.media_type ?? ''}
                      .shareTitle=${title}
                      .chromeless=${open}
                    ></player-surface>
                    ${this.narrow && !open
                      ? html`<button
                          class="strip"
                          aria-label="Show details"
                          @click=${() => this.setSheet('half')}
                        >
                          <span class="ttl">${title}</span>
                          <span class="open-detail">
                            <g-icon name="chev-up"></g-icon>Detail
                          </span>
                        </button>`
                      : nothing}
                  </div>
                  ${this.narrow ? nothing : this.renderKnowledge(d)}
                </div>
                ${n > 1
                  ? html`<div class="nav">
                      <button aria-label="Previous item" @click=${() => this.step(-1)}>
                        <g-icon name="chev-up"></g-icon>
                      </button>
                      <button aria-label="Next item" @click=${() => this.step(1)}>
                        <g-icon name="chev-down"></g-icon>
                      </button>
                    </div>`
                  : nothing}
              </div>
              ${this.narrow
                ? html`<g-sheet
                    .state=${this.sheet}
                    .detents=${DETENTS}
                    .ladder=${LADDER}
                    @sheet-state=${(e: CustomEvent<string>) =>
                      this.setSheet(e.detail as SheetState)}
                  >
                    <g-button
                      slot="end"
                      icon-only
                      label="Close"
                      @click=${() => this.setSheet('closed')}
                    >
                      <g-icon name="x"></g-icon>
                    </g-button>
                    ${this.renderKnowledge(d)}
                  </g-sheet>`
                : nothing}
            </div>
          `}
      ${this.confirming && d
        ? html`<g-dialog
            heading=${`Remove “${clean(d.title) || 'Untitled'}”?`}
            @g-close=${() => (this.confirming = false)}
          >
            <p class="warn">Removes it from your library.</p>
            <g-button slot="actions" @click=${() => this.dialog?.close()}>Cancel</g-button>
            <g-button slot="actions" variant="danger" @click=${() => void this.confirmDelete()}>
              Remove
            </g-button>
          </g-dialog>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-detail': GDetail;
  }
}
